import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { apiFetch } from "@/lib/api";

const EpisodeContext = createContext();

export function EpisodeProvider({ children }) {
  const [episode, setEpisode] = useState(null);
  const [episodeSaving, setEpisodeSaving] = useState(false);
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const baseScriptRef = useRef("");

  const updateEpisode = (newEpisode) => {
    if (!episode && newEpisode?.script !== undefined) {
      baseScriptRef.current = newEpisode.script || "";
    }
    setEpisode(newEpisode);
  };

  const debouncedSave = useCallback(
    (saveFunction, key, delay = 2000) => {
      // Clear existing timeout for this key
      if (saveTimeouts[key]) {
        console.log(`Clearing key: ${key}`);
        clearTimeout(saveTimeouts[key]);
      }

      // Set new timeout
      const timeoutId = setTimeout(async () => {
        console.log(`Saving on key: ${key}`);
        try {
          await saveFunction();
        } catch (error) {
          console.error("Error saving:", error);
          setEpisodeSaving(false);
        }

        // Clean up the timeout reference
        setSaveTimeouts((prev) => {
          const newTimeouts = { ...prev };
          delete newTimeouts[key];
          return newTimeouts;
        });
      }, delay);

      // Store the timeout ID
      setSaveTimeouts((prev) => ({
        ...prev,
        [key]: timeoutId,
      }));
    },
    [saveTimeouts],
  );

  const fetchEpisode = async () => {
    try {
      const response = await apiFetch(
        `/api/shows/${episode.show}/episodes/${episode._id}`,
      );
      const data = await response.json();
      baseScriptRef.current = data.script || "";
      updateEpisode(data);
    } catch (error) {
      console.error("Error fetching episode:", error);
    }
  };

  const saveEpisodeField = useCallback(
    async (data) => {
      try {
        setEpisodeSaving(true);
        const response = await apiFetch(
          `/api/shows/${episode.show}/episodes/${episode._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
          },
        );
        if (!response.ok) {
          throw new Error("Failed to save episode");
        }
      } catch (error) {
        console.error("Error saving episode:", error);
      } finally {
        setEpisodeSaving(false);
      }
    },
    [episode],
  );

  const saveScriptField = useCallback(
    async (currentScript) => {
      if (baseScriptRef.current === currentScript) return;

      try {
        setEpisodeSaving(true);
        const response = await apiFetch(
          `/api/shows/${episode.show}/episodes/${episode._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              scriptBase: baseScriptRef.current,
              scriptCurrent: currentScript,
            }),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to save script");
        }

        const result = await response.json();
        baseScriptRef.current = result.script;
      } catch (error) {
        console.error("Error saving script:", error);
      } finally {
        setEpisodeSaving(false);
      }
    },
    [episode],
  );

  const updateEpisodeField = (prop, value) => {
    updateEpisode({ ...episode, [prop]: value });
    const key = `episode-${prop}`;
    if (prop === "script") {
      debouncedSave(() => saveScriptField(value), key);
    } else {
      debouncedSave(() => saveEpisodeField({ [prop]: value }), key);
    }
  };

  useEffect(() => {
    return () => {
      Object.values(saveTimeouts).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
    };
  }, [saveTimeouts]);

  return (
    <EpisodeContext.Provider
      value={{
        episode,
        updateEpisode,
        updateEpisodeField,
        fetchEpisode,
        episodeSaving,
      }}>
      {children}
    </EpisodeContext.Provider>
  );
}

export function useEpisode() {
  const context = useContext(EpisodeContext);
  if (!context) {
    throw new Error("useEpisode must be used within a EpisodeProvider");
  }
  return context;
}
